import {clampItemQuantities} from '../components/ScanpackScanning/utils/itemQuantities';
export const shuffleItemToFront = (order, itemToScan) => {
  const unScannedItems = [...(order.unscanned_items || [])];

  const topLevelIndex = unScannedItems.findIndex(item => item.sku === itemToScan.sku);

  if (topLevelIndex === 0) {
    return order;
  }

  if (topLevelIndex > 0) {
    const [item] = unScannedItems.splice(topLevelIndex, 1);
    unScannedItems.unshift(item);
    return { ...order, unscanned_items: unScannedItems };
  }

  for (let i = 0; i < unScannedItems.length; i++) {
    const parent = unScannedItems[i];
    if (parent.child_items?.length > 0) {
      const childIndex = parent.child_items.findIndex(c => c.sku === itemToScan.sku);
      if (childIndex >= 0) {
        const childItems = [...parent.child_items];
        const [child] = childItems.splice(childIndex, 1);
        childItems.unshift(child);
        unScannedItems[i] = { ...parent, child_items: childItems };
        const [parentItem] = unScannedItems.splice(i, 1);
        unScannedItems.unshift(parentItem);
        return { ...order, unscanned_items: unScannedItems };
      }
    }
  }

  return order;
};

export const restoreNotFulfilledItems = (order) => {
  const notFulfilledItems = order?.not_fulfilled_items || [];
  if ((order?.unscanned_items?.length ?? 1) > 0 || notFulfilledItems.length === 0) {
    return order;
  }
  const restoredItems = notFulfilledItems.map(item => {
    const restoredItem = JSON.parse(JSON.stringify(item));
    if (restoredItem.skipped_qty > 0) {
      restoredItem.qty_remaining = restoredItem.skipped_qty;
      restoredItem.total_qty = restoredItem.skipped_qty;
      restoredItem.scanned_qty = 0;
      restoredItem.skipped_qty = 0;
    }
    restoredItem.from_not_fulfilled = true;
    restoredItem.child_items = item.child_items || [];
    return restoredItem;
  });
  return { ...order, unscanned_items: restoredItems };
};

export const getItemToProcess = (firstItem) => {
  if (firstItem?.child_items && firstItem.child_items.length > 0) {
    return firstItem.child_items[0];
  }
  return firstItem;
};

export const getActiveBoxName = (Order, activeBoxIndex = 0, defaultBox = 'BOX 1') => {
  const idx = activeBoxIndex || Order?.activeBoxIndex || 0;
  return Order?.boxes?.[idx]?.name || defaultBox;
};

export const updateOrderAfterScan = (currentOrder, updatedItem, scannedItems, unScannedItems, boxId = null, boxQty = 1) => {
  const updated = {
    ...currentOrder,
    scanned_items: scannedItems,
    unscanned_items: unScannedItems
  };
  if (boxId) {
    updated.order_item_boxes = addBoxItemToOrder(currentOrder, boxId, updatedItem, boxQty);
  }
  return updated;
};

export const getSuccessTimeout = (scanPackSettings) =>
  (scanPackSettings?.success_image_time || 0.3) * 400;

export const getFailTimeout = (scanPackSettings) =>
  (scanPackSettings?.fail_image_time || 0.3) * 400;

export const getPackingCount = (item, barcodeValue) => {
  if (!barcodeValue) return 1;
  const match = item.barcodes?.find(b =>
    b.barcode?.toLowerCase().trim() === barcodeValue.toLowerCase().trim()
  );
  return parseInt(match?.packing_count) || 1;
};

export const adjustForPackingCount = (updatedItem, packingCount) => {
  if (packingCount <= 1) return updatedItem;
  return clampItemQuantities({
    ...updatedItem,
    scanned_qty: updatedItem.scanned_qty + (packingCount - 1),
    qty_remaining: updatedItem.qty_remaining - (packingCount - 1)
  });
};

// ============================================
// Multi-Box Helpers
// ============================================

export const isMultiBoxEnabled = (generalSettings) => {
  return generalSettings?.multi_box_shipments === true;
};

export const getActiveBoxId = (Order, activeBoxIndex) => {
  return Order?.boxes?.[activeBoxIndex]?.id || null;
};

export const getBoxCount = (Order) => {
  return Order?.boxes?.length || 0;
};

export const getBoxItemCountFromOrder = (currentOrder, boxId) => {
  if (!boxId) return 0;
  const items = currentOrder?.order_item_boxes || [];
  return items
    .filter(entry => entry.box_id === boxId)
    .reduce((sum, entry) => sum + (entry.item_qty || 0), 0);
};

export const canCreateNewBox = (currentOrder) => {
  const boxes = currentOrder?.boxes || [];
  if (boxes.length === 0) return true;
  const lastBox = boxes[boxes.length - 1];
  const itemsInLastBox = (currentOrder?.order_item_boxes || []).filter(
    entry => entry.box_id === lastBox.id
  );
  return itemsInLastBox.length > 0;
};

export const addBoxItemToOrder = (currentOrder, boxId, item, qty = 1) => {
  const existing = (currentOrder?.order_item_boxes || []);
  const matchEntry = (entry) =>
    entry.box_id === boxId && entry.order_item_id === item?.order_item_id && entry.product_id === item?.product_id;
  const found = existing.find(matchEntry);
  if (found) {
    return existing.map(entry =>
      matchEntry(entry)
        ? { ...entry, item_qty: (entry.item_qty || 0) + qty }
        : entry
    );
  }
  return [...existing, {
    box_id: boxId,
    order_item_id: item?.order_item_id,
    product_id: item?.product_id,
    item_qty: qty
  }];
};

export const ensureOrderBoxFields = (order) => {
  if (!order) return order;
  return {
    ...order,
    boxes: order.boxes || [],
    order_item_boxes: order.order_item_boxes || []
  };
};

export const replaceBoxTempId = (currentOrder, tempId, realId) => {
  if (!currentOrder) return currentOrder;
  const boxes = (currentOrder.boxes || []).map(b =>
    b.id === tempId ? { ...b, id: realId } : b
  );
  const orderItemBoxes = (currentOrder.order_item_boxes || []).map(entry =>
    entry.box_id === tempId ? { ...entry, box_id: realId } : entry
  );
  return { ...currentOrder, boxes, order_item_boxes: orderItemBoxes };
};

export const getLogBoxId = (Order, activeBoxIndex, generalSettings) => {
  if (!isMultiBoxEnabled(generalSettings)) return null;
  const boxId = getActiveBoxId(Order, activeBoxIndex);
  if (!boxId || (typeof boxId === 'string' && boxId.startsWith('temp-'))) return null;
  return boxId;
};

export const attachBoxIdToLog = (logData, order, activeBoxIndex, generalSettings) => {
  const boxId = getLogBoxId(order, activeBoxIndex, generalSettings);
  if (boxId) logData.box_id = boxId;
  return logData;
};

